import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridFooter,
  GridFooterContainer,
  GridPaginationModel,
  GridRowSelectionModel
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import './CounterPartyFilterDialogStyle.css';
import CounterPartyMaritimeDirectoryPortEditorButton from './CounterPartyMaritimeDirectoryPortEditorButton';
import { counterPartyModel, CounterPartyType, CounterPartyTypeLabels, CounterPartyTypes, portModel } from 'model';
import { useApolloErrorHandler } from 'hooks';
import CounterPartySelectorContext from './CounterPartySelectorContext';
import useDebounce from 'hooks/useDebounce';
import { GET_COUNTER_PARTIES_WITH_PORT, GetCounterPartiesOutput } from 'api';
import { PageSizeOptions } from 'shared/constants';
import capitalizeEveryWord from '../../../../../shared/stringUtils/capitalizeEveryWord';
import {
  TXT_ADD,
  TXT_CANCEL,
  TXT_CONTACT,
  TXT_INCLUDE_DISABLED_PORTS,
  TXT_NAME,
  TXT_OK,
  TXT_PORT,
  TXT_PORTS,
  TXT_SEARCH
} from '../../../../../shared/translations';
import { CounterPartyEditor } from 'pages/CounterParty/CounterPartyEditor';
import { MaritimeDirectoryEditor } from 'pages/MaritimeDirectories/MaritimeDirectoryEditor';
import { PortSelector } from 'components/PortSelector';
import { CountrySelector } from 'components/CountrySelector';
import { GridNoResultsOverlay } from 'components/GridNoResultsOverlay';
import capitalize from '../../../../../shared/stringUtils/capitalize';
import {
  ADD_PORT_TO_COUNTER_PARTY,
  AddPortToCounterPartyInput,
  AddPortToCounterPartyResponse
} from 'api/graphql/maritimeDirectoryPort/maritimeDirectoryPort.mutations';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';

interface CounterPartyFilterDialogProps {
  onClose: () => void;
}

interface Toggle {
  counterPartyEditor: boolean;
  maritimeDirectoryEditor: boolean;
}

const allowedPortSelection: CounterPartyType[] = ['ALL', 'AGENT'];
const hasMaritimeDirectories: CounterPartyType[] = ['AGENT'];

const CounterPartyFilterDialog = (props: CounterPartyFilterDialogProps) => {
  const { t } = useTranslation();
  const { apolloErrorHandler } = useApolloErrorHandler();
  const {
    counterParty,
    port: defaultPort,
    allowAdd,
    onChange,
    counterPartyType,
    disablePortFilter,
    disableCountryFilter
  } = useContext(CounterPartySelectorContext);

  const [port, setPort] = useState<Partial<portModel> | undefined>(defaultPort);
  const [country, setCountry] = useState<string | null>(null);
  const [search, setSearch] = useState<string>();
  const debouncedSearch = useDebounce((txt?: string) => setSearch(txt));

  const [data, setData] = useState<GetCounterPartiesOutput | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PageSizeOptions[0]);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>();
  const [toggle, setToggle] = useState<Toggle>({
    counterPartyEditor: false,
    maritimeDirectoryEditor: false
  });
  const [selected, setSelected] = useState<counterPartyModel | undefined>(counterParty);
  const [includeDisabledPorts, setIncludeDisabledPorts] = useState(false);

  const changeToggle = (key: keyof Toggle) => () => setToggle((prev) => ({ ...prev, [key]: !prev[key] }));

  const allowPortSelection = useMemo(() => allowedPortSelection.includes(counterPartyType), [counterPartyType]);

  const hasMaritimeDirectory = useMemo(() => hasMaritimeDirectories.includes(counterPartyType), [counterPartyType]);

  const filter = useMemo(() => {
    let result: unknown[] = [];

    if (counterPartyType) {
      result = [
        ...result,
        {
          counterPartyTypes: {
            some: {
              id: {
                eq: CounterPartyTypes[counterPartyType]
              }
            }
          }
        }
      ];
    }

    if ((search?.length || 0) > 0) {
      result = [
        ...result,
        {
          name: {
            contains: search
          }
        }
      ];
    }

    if (port) {
      result = [
        ...result,
        {
          maritimeDirectories: {
            some: {
              ports: {
                some: {
                  and: [
                    {
                      port: {
                        id: {
                          eq: port.id
                        }
                      }
                    },
                    ...(!includeDisabledPorts ? [{ isActive: { eq: true } }] : [])
                  ]
                }
              }
            }
          }
        }
      ];
    }

    if (country) {
      result = [
        ...result,
        {
          maritimeDirectories: {
            some: {
              ports: {
                some: {
                  and: [
                    {
                      port: {
                        country: {
                          eq: country
                        }
                      }
                    },
                    ...(!includeDisabledPorts ? [{ isActive: { eq: true } }] : [])
                  ]
                }
              }
            }
          }
        }
      ];
    }

    return result.length === 0 ? null : { and: result };
  }, [counterPartyType, search, port, country, includeDisabledPorts]);

  const {
    loading,
    data: response,
    error,
    refetch
  } = useQuery<GetCounterPartiesOutput>(GET_COUNTER_PARTIES_WITH_PORT, {
    fetchPolicy: 'network-only',
    variables: {
      skip: page * pageSize,
      take: pageSize,
      where: filter
    }
  });

  const [savePortToCounterParty, { loading: saving }] = useMutation<
    AddPortToCounterPartyResponse,
    AddPortToCounterPartyInput
  >(ADD_PORT_TO_COUNTER_PARTY, {
    onError: apolloErrorHandler,
    onCompleted: () => onUpdate()
  });

  const gridColumns = useMemo(() => {
    let columns: GridColDef<Partial<counterPartyModel>>[] = [
      {
        field: 'name',
        headerName: capitalizeEveryWord(t(TXT_NAME)),
        flex: 1,
        cellClassName: 'cell-column',
        valueGetter: (params) => params.row.name
      }
    ];

    if (allowPortSelection) {
      columns = [
        ...columns,
        {
          field: 'ports',
          headerName: capitalizeEveryWord(t(TXT_PORTS)),
          flex: 1,
          cellClassName: 'cell-column',
          renderCell: (params) => {
            const ports = (params.row.maritimeDirectories || []).flatMap((x) => x.ports || []);
            return (
              <Box display="flex" flexDirection="row" gap={1} flexWrap="wrap" pt={1} pb={1}>
                {ports.map((x) => (
                  <Chip
                    key={x.portId}
                    icon={x.isActive ? <CheckIcon /> : <BlockIcon />}
                    label={x.port?.name}
                    color={x.isActive ? 'success' : 'error'}
                  />
                ))}
              </Box>
            );
          }
        },
        {
          field: 'contacts',
          headerName: capitalizeEveryWord(t(TXT_CONTACT)),
          flex: 1,
          cellClassName: 'cell-column',
          valueGetter: (params) => (params.row.maritimeDirectories || []).flatMap((x) => x.contact?.name).join(', ')
        },
        {
          field: 'maritimeDirectory',
          headerName: '',
          width: 100,
          cellClassName: 'cell-column',
          align: 'center',
          renderCell: (params) => {
            const maritimeDirectories = params.row.maritimeDirectories || [];
            if (maritimeDirectories.length === 0) {
              return null;
            }
            const filterPort = port || defaultPort;
            let maritimeDirectory = undefined;
            if (filterPort) {
              maritimeDirectory = maritimeDirectories.find((x) =>
                (x.ports || []).some((p) => p.portId === filterPort.id)
              );
            }

            return (
              <CounterPartyMaritimeDirectoryPortEditorButton
                counterParty={params.row}
                maritimeDirectoryId={(maritimeDirectory || maritimeDirectories[0]).id || ''}
                refetch={refetch}
              />
            );
          }
        }
      ];
    }

    return columns;
  }, [allowPortSelection, defaultPort, port, refetch, t]);

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const customFooter = () => (
    <GridFooterContainer
      sx={(theme) => ({
        padding: `0 ${theme.spacing(1)}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      })}
    >
      {allowAdd ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={changeToggle('counterPartyEditor')}
        >
          {capitalizeEveryWord(t(TXT_ADD))}
        </Button>
      ) : (
        <div />
      )}
      <GridFooter
        sx={{
          border: 'none'
        }}
      />
    </GridFooterContainer>
  );

  const onPortChange = (value: Partial<portModel> | null) => {
    setPort(value || undefined);
  };

  const afterCounterPartyCreated = (added: counterPartyModel) => {
    setSelected(added);
    setSelectedRows([added.id!]);
    if ((added.counterPartyTypes || []).some((x) => x.id === CounterPartyTypes.AGENT)) {
      changeToggle('maritimeDirectoryEditor')();
    } else {
      refetch();
    }
  };

  const onUpdate = useCallback(() => {
    onChange(selected);
    props.onClose();
  }, [onChange, selected, props]);

  const onAccept = useCallback(() => {
    if (hasMaritimeDirectory && selected && defaultPort) {
      const ports = (selected.maritimeDirectories || []).flatMap((x) => x.ports || []);
      if (!ports.some((x) => x.portId === defaultPort.id)) {
        savePortToCounterParty({
          variables: {
            input: {
              counterPartyId: selected.id!,
              portId: defaultPort.id!
            }
          }
        });
        return;
      }
    }
    onUpdate();
  }, [defaultPort, hasMaritimeDirectory, onUpdate, savePortToCounterParty, selected]);

  const onRowSelected = (rowSelectionModel: GridRowSelectionModel) => {
    if (rowSelectionModel.length === 1) {
      setSelectedRows(rowSelectionModel);
      setSelected(data?.counterParties?.items.find((item) => item.id === rowSelectionModel[0]));
    } else {
      setSelectedRows([]);
      setSelected(undefined);
    }
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSearch(e.target.value);
  };

  const onIncludeDisabledPortsChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIncludeDisabledPorts(checked);
  };

  useEffect(() => {
    if (port?.country) {
      setCountry(port.country);
    }
  }, [port]);

  useEffect(() => {
    if (country && port?.country !== country) {
      setPort(undefined);
    }
  }, [country]);

  useEffect(() => {
    setPage(0);
  }, [search, country, port, pageSize, includeDisabledPorts]);

  useEffect(() => {
    refetch({
      skip: page * pageSize,
      take: pageSize,
      where: filter
    });
  }, [filter, page, pageSize, refetch]);

  useEffect(() => {
    apolloErrorHandler(error);
  }, [apolloErrorHandler, error]);

  useEffect(() => {
    if (loading) {
      return;
    }
    setData(response ? { ...response } : null);
    if (!selectedRows && selected?.id) {
      setSelectedRows([selected.id]);
    }
  }, [loading, response, selected?.id, selectedRows]);

  return (
    <>
      {toggle.counterPartyEditor ? (
        <CounterPartyEditor
          defaultCounterPartyTypes={[counterPartyType]}
          onClose={changeToggle('counterPartyEditor')}
          afterSave={afterCounterPartyCreated}
        />
      ) : null}
      {toggle.maritimeDirectoryEditor ? (
        <MaritimeDirectoryEditor
          ports={port ? [port] : []}
          counterParty={selected}
          counterPartyId={selected?.id}
          onClose={changeToggle('maritimeDirectoryEditor')}
          afterSave={refetch}
        />
      ) : null}
      <Dialog open maxWidth="xl" fullWidth>
        <DialogTitle>{capitalizeEveryWord(t(CounterPartyTypeLabels[counterPartyType]))}</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <PortSelector
                port={port}
                onSelectedChange={onPortChange}
                country={country || undefined}
                label={capitalizeEveryWord(t(TXT_PORT))}
                disabled={disablePortFilter}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CountrySelector
                country={country || undefined}
                onSelectedChange={setCountry}
                disabled={disableCountryFilter}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label={capitalizeEveryWord(t(TXT_SEARCH))} onChange={onSearchChange} />
            </Grid>
            <Grid item xs={12} sx={{ height: 'calc(100vh - 300px)' }}>
              <DataGrid
                getRowId={(x) => x.id!}
                columns={gridColumns}
                rows={data?.counterParties?.items || []}
                rowCount={data?.counterParties?.totalCount || 0}
                getRowHeight={() => 'auto'}
                paginationModel={{
                  page,
                  pageSize
                }}
                onPaginationModelChange={onPaginationModelChange}
                pageSizeOptions={PageSizeOptions}
                loading={loading}
                disableColumnFilter
                disableColumnMenu
                checkboxSelection
                hideFooterSelectedRowCount
                keepNonExistentRowsSelected
                paginationMode="server"
                slots={{
                  noRowsOverlay: GridNoResultsOverlay,
                  footer: customFooter
                }}
                onRowSelectionModelChange={onRowSelected}
                rowSelectionModel={selectedRows}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between'
          }}
        >
          {allowPortSelection ? (
            <FormControlLabel
              control={
                <Switch color="primary" checked={includeDisabledPorts} onChange={onIncludeDisabledPortsChange} />
              }
              label={capitalize(t(TXT_INCLUDE_DISABLED_PORTS))}
              sx={{
                pl: 2
              }}
            />
          ) : (
            <div></div>
          )}
          <div>
            <Button color="secondary" onClick={props.onClose} disabled={saving}>
              {t(TXT_CANCEL)}
            </Button>
            <Button
              color="primary"
              onClick={onAccept}
              disabled={!selected || saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {t(TXT_OK)}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CounterPartyFilterDialog;
